import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import axios from "axios";
import dayjs from "dayjs";
import {
  UserOutlined,
  TeamOutlined,
  UploadOutlined,
  DownloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
  CloseOutlined,
  CheckOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
  MenuOutlined,
  ApartmentOutlined,
  EnvironmentOutlined,
  MailOutlined,
  LockOutlined,
  IdcardOutlined,
  FileTextOutlined,
  DesktopOutlined,
  UserAddOutlined,
  HistoryOutlined
} from "@ant-design/icons";

import Navbar from "./Navbar";
// import Sidebar from "./Sidebar";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_BASE = `${API_BASE_URL}/api`;

const BREAKPOINT = 768;
const LS_KEY = "dma_sb_collapsed";

// Global DMA roles constant (these will always need desk assignments)
const DMA_ROLES = [4, 6, 8, 9, 10];

// Desk colors for better UI
const DESK_COLORS = [
  "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4",
  "#FFEAA7", "#DDA0DD", "#98D8C8", "#F7DC6F"
];

const CreateUserPage = () => {
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= BREAKPOINT);
  const [sbCollapsed, setSbCollapsed] = useState(() => {
    const saved = sessionStorage.getItem("sidebar_collapsed");
    return saved ? JSON.parse(saved) : true; // Default to collapsed
  });

  const [activeTab, setActiveTab] = useState("view");
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);

  const [csvFile, setCsvFile] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);

  const [csvPreview, setCsvPreview] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSummary, setUploadSummary] = useState(null);

  const [stateOptions, setStateOptions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [ulbOptions, setUlbOptions] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedUlb, setSelectedUlb] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // Database-driven options
  const [roleOptions, setRoleOptions] = useState([]);
  const [deskOptions, setDeskOptions] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingDesks, setLoadingDesks] = useState(false);

  // Mobile-specific states
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  // Form state - WITH MULTIPLE DESK ASSIGNMENTS
  const [formData, setFormData] = useState({
    username: "",
    full_name: "",
    email: "",
    password: "",
    role: "",
    designation: "",
    desk_ids: [], // Array of desk IDs for multiple desk assignments
    desk_names: [], // Array of desk names
    state: "",
    division: "",
    district: "",
    ulb_id: "",
    ulb_name: "",
    ulb_type: "",
    has_form_builder_access: false,
    status: "active" // Default status
  });

  const fetchRoles = useCallback(async () => {
    setLoadingRoles(true);
    try {
      const response = await axios.get(`${API_BASE}/meta/roles`, {
        timeout: 5000
      });

      if (response.data.success && response.data.roles) {
        // Assign colors based on role ID for UI consistency
        const rolesWithColors = response.data.roles.map(role => {
          let color = "blue";
          // Assign colors based on role ID ranges
          if (role.id === 1 || role.id === 7) color = "blue";
          else if (role.id === 2) color = "green";
          else if (role.id === 3) color = "purple";
          else if (role.id === 4) color = "orange";
          else if (role.id === 5) color = "red";
          else if (role.id === 6) color = "pink";
          else if (role.id === 8) color = "purple";
          else if (role.id === 9) color = "darkblue";
          else if (role.id === 10) color = "teal";

          return {
            value: role.id,
            label: role.name,
            color: color
          };
        });

        // Store ALL roles for viewing
        setRoleOptions(rolesWithColors);
        console.log("✅ Fetched all roles from database:", rolesWithColors.length);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      // Fallback to default roles if API fails
      setRoleOptions([
        { value: 8, label: "DMA Admin", color: "purple" },
        { value: 9, label: "DMA System Admin", color: "darkblue" },
        { value: 4, label: "DMA Deputy Commissioner", color: "orange" },
        { value: 6, label: "DMA Desk User", color: "pink" },
        { value: 3, label: "Divisional Joint Commissioner", color: "purple" },
        { value: 2, label: "District Joint Commissioner", color: "green" },
        { value: 7, label: "ULB Chief Officer", color: "pink" },
        { value: 1, label: "ULB Users", color: "blue" },
        { value: 5, label: "Support Admin", color: "red" },
        { value: 10, label: "Support User", color: "teal" },
      ]);
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  // ===== FETCH DESKS FROM DATABASE =====
  const fetchDesks = useCallback(async () => {
    setLoadingDesks(true);
    try {
      const response = await axios.get(`${API_BASE}/meta/desks`, {
        timeout: 5000
      });

      if (response.data.success && response.data.desks) {
        // Format desks for UI with colors
        const desksWithColors = response.data.desks.map((desk, index) => ({
          id: desk.id,
          name: desk.name,
          desk_number: desk.desk_number,
          color: DESK_COLORS[index % DESK_COLORS.length] // Assign colors cyclically
        }));

        setDeskOptions(desksWithColors);
        console.log("✅ Fetched desks from database:", desksWithColors.length);
      }
    } catch (error) {
      console.error("Error fetching desks:", error);
      // Fallback to default desks if API fails
      setDeskOptions([
        { id: 1, name: "Desk 1 - Administration", color: DESK_COLORS[0] },
        { id: 2, name: "Desk 2 - Finance", color: DESK_COLORS[1] },
        { id: 3, name: "Desk 3 - Engineering", color: DESK_COLORS[2] },
        { id: 4, name: "Desk 4 - Health", color: DESK_COLORS[3] },
        { id: 5, name: "Desk 5 - Education", color: DESK_COLORS[4] },
        { id: 6, name: "Desk 6 - Revenue", color: DESK_COLORS[5] },
        { id: 7, name: "Desk 7 - Planning", color: DESK_COLORS[6] },
        { id: 8, name: "Desk 8 - Legal", color: DESK_COLORS[7] },
      ]);
    } finally {
      setLoadingDesks(false);
    }
  }, []);

  // ===== SIDEBAR STATE MANAGEMENT =====
  // useEffect(() => {
  //   const handleStorageChange = (e) => {
  //     if (e.key === "sidebar_collapsed") {
  //       const newValue = e.newValue ? JSON.parse(e.newValue) : true;
  //       setSbCollapsed(newValue);
  //     }
  //   };

  //   window.addEventListener('storage', handleStorageChange);

  //   // REMOVED the interval that was causing constant state resets
  //   // This was forcing the sidebar to collapse/expand based on sessionStorage every 500ms

  //   return () => {
  //     window.removeEventListener('storage', handleStorageChange);
  //     // Removed clearInterval
  //   };
  // }, []); // REMOVED sbCollapsed dependency to prevent loops

  // Save to sessionStorage when state changes (only this direction)
  useEffect(() => {
    try {
      sessionStorage.setItem(LS_KEY, String(sbCollapsed));
    } catch { }
  }, [sbCollapsed]);

  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 50);
    return () => clearTimeout(timer);
  }, [sbCollapsed]);

  // ========== RESPONSIVE HANDLING ==========
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) {
        setShowMobileFilters(false);
        setShowMobileSearch(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ========== FETCH FUNCTIONS ==========
  const fetchUlbs = useCallback(async (districtId) => {
  if (!districtId) {
    setUlbOptions([]);
    setSelectedUlb("");
    setFormData(prev => ({ ...prev, ulb_id: "", ulb_name: "", ulb_type: "" }));
    return;
  }

  try {
    const response = await axios.get(`${API_BASE}/meta/ulbs?district_id=${districtId}`, {
      timeout: 5000
    });
    
    // Ensure each ULB has proper numeric id
    const ulbs = (response.data.ulbs || []).map(ulb => ({
      id: parseInt(ulb.id),
      ulb_name: ulb.ulb_name,
      ulb_type: ulb.ulb_type || ""
    }));
    
    setUlbOptions(ulbs);
    console.log("📋 ULBs fetched:", ulbs.length);
  } catch (error) {
    console.error('Error fetching ULBs:', error);
    alert("Failed to load ULBs");
  }
}, []);

  const fetchStates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/meta/states`, { timeout: 5000 });
      setStateOptions(res.data?.data || []);
    } catch (err) {
      console.error("States fetch error:", err);
    }
  }, []);

  const fetchDivisions = useCallback(async (stateId) => {
    if (!stateId) {
      setDivisionOptions([]);
      setDistrictOptions([]);
      setUlbOptions([]);
      setSelectedDivision("");
      setSelectedDistrict("");
      setSelectedUlb("");
      return;
    }
    try {
      const res = await axios.get(`${API_BASE}/meta/divisions?state_id=${stateId}`, { timeout: 5000 });
      setDivisionOptions(res.data?.data || []);
    } catch (err) {
      console.error("Divisions fetch error:", err);
    }
  }, []);

  const fetchDistricts = useCallback(async (divisionId) => {
    if (!divisionId) {
      setDistrictOptions([]);
      setUlbOptions([]);
      setSelectedDistrict("");
      setSelectedUlb("");
      return;
    }
    try {
      const res = await axios.get(`${API_BASE}/meta/districts?division_id=${divisionId}`, { timeout: 5000 });
      setDistrictOptions(res.data?.data || []);
    } catch (err) {
      console.error("Districts fetch error:", err);
    }
  }, []);

  // FETCH USERS - UPDATED for new desk structure
  const fetchUsers = useCallback(async () => {
    setViewLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.append('search', search.trim());

      const response = await axios.get(`${API_BASE}/admin/users-list?${params.toString()}`, {
        timeout: 15000
      });

      const users = response.data.users || [];
      console.log("📊 Fetched users with designations:", users.filter(u => u.designation).length);
      console.log("📊 Fetched users with desk assignments:", users.filter(u => u.desk_assignments?.length > 0).length);

      // Add status field if not present
      users.forEach(user => {
        if (!user.status) user.status = 'active';
      });

      setUsersList(users);
      applyFilters(users, search, roleFilter);
    } catch (error) {
      console.error("Users fetch ERROR:", error);
      alert(`Failed to load users: ${error.response?.data?.error || error.message}`);
    } finally {
      setViewLoading(false);
    }
  }, [search, roleFilter]);

  // ========== APPLY FILTERS FUNCTION ==========
  const applyFilters = useCallback((users, searchTerm, roleFilterValue) => {
    let filtered = [...users];

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(user => {
        const matchesBasic =
          user.username?.toLowerCase().includes(searchLower) ||
          user.full_name?.toLowerCase().includes(searchLower) ||
          user.name?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.designation?.toLowerCase().includes(searchLower) ||
          user.ulb_name?.toLowerCase().includes(searchLower);

        const matchesDesk = user.desk_assignments?.some(desk =>
          desk.name?.toLowerCase().includes(searchLower) ||
          `desk ${desk.id}`.includes(searchLower) ||
          `desk ${desk.desk_number}`.includes(searchLower)
        );

        return matchesBasic || matchesDesk;
      });
    }

    if (roleFilterValue) {
      filtered = filtered.filter(user =>
        parseInt(user.role) === parseInt(roleFilterValue)
      );
    }

    setFilteredUsers(filtered);
  }, []);

  // ========== EFFECT FOR FILTERING ==========
  useEffect(() => {
    applyFilters(usersList, search, roleFilter);
  }, [usersList, search, roleFilter, applyFilters]);

  // ========== DESK SELECTION HANDLER ==========
  const handleDeskSelection = (deskId) => {
    setFormData(prev => {
      const currentDeskIds = [...prev.desk_ids];
      const deskIndex = currentDeskIds.indexOf(deskId);

      if (deskIndex === -1) {
        currentDeskIds.push(deskId);
      } else {
        currentDeskIds.splice(deskIndex, 1);
      }

      // Sort desk IDs for consistent display
      currentDeskIds.sort((a, b) => a - b);

      // Get desk names with colors
      const deskNames = currentDeskIds.map(id => {
        const desk = deskOptions.find(d => d.id === id);
        return desk ? desk.name : `Desk ${id}`;
      });

      return {
        ...prev,
        desk_ids: currentDeskIds,
        desk_names: deskNames
      };
    });
  };

  // ========== CSV PREVIEW ==========
  const previewCsv = async () => {
    if (!csvFile) {
      alert("Please select a CSV file first");
      return;
    }
    setBulkLoading(true);
    try {
      const text = await csvFile.text();
      const filteredText = text
        .split(/\r?\n/)
        .filter((line) => line.trim() && !line.trim().startsWith("#"))
        .join("\n");

      const parsed = Papa.parse(filteredText, { header: true });
      const previewData = (parsed.data || [])
        .filter((row) => row.username)
        .slice(0, 20)
        .map((row, index) => {
          // Parse desk IDs - can be single or comma-separated
          let deskIds = [];
          const deskIdRaw = row.desk_id || row.desk_ids || "";
          if (deskIdRaw) {
            deskIds = deskIdRaw.toString().split(',')
              .map(id => parseInt(id.trim()))
              .filter(id => !isNaN(id));
          }

          // Get desk names
          const deskNames = deskIds
            .map(id => deskOptions.find(d => d.id === id)?.name || `Desk ${id}`)
            .filter(name => name);

          // Get role name
          const roleId = parseInt(row.role_id || row.role);
          const roleName = roleOptions.find(r => r.value === roleId)?.label || "Unknown Role";
          const isValid = row.username && row.password && !isNaN(roleId);

          return {
            key: index,
            row_index: index + 1,
            username: row.username?.trim() || "❌ MISSING",
            full_name: row.full_name || row.username || "N/A",
            email: row.email || `${row.username}@dma.gov.in`,
            role_raw: row.role_id || row.role || "❌ NONE",
            role_id: roleId || "❌ INVALID",
            role_name: roleName,
            designation: row.designation || "Not specified",
            desk_ids: deskIds.join(', ') || "None",
            desk_names: deskNames.join(', ') || "Not assigned",
            state_name: row.state_name || row.state || "Required",
            has_form_builder_access: row.has_form_builder_access === "true" || row.has_form_builder_access === "1" ? "YES" : "NO",
            has_password: !!row.password,
            status: row.status || "active",
            isValid: isValid ? "READY" : "INVALID"
          };
        });
      setCsvPreview(previewData);
      setPreviewVisible(true);
    } catch (error) {
      console.error("CSV Parse Error:", error);
      alert("Failed to parse CSV file");
    } finally {
      setBulkLoading(false);
    }
  };

  // Helper to get desk name by ID
  const getDeskName = (deskId) => {
    if (!deskId) return null;
    const desk = deskOptions.find(d => d.id === parseInt(deskId));
    return desk ? desk.name : null;
  };

  // ========== BULK UPLOAD ==========
const onBulkUpload = async () => {
  if (!csvFile) {
    alert("Please select a CSV file first");
    return;
  }
  
  // Password validation function for bulk upload
  const validatePassword = (password, rowIndex) => {
  if (!password) return { valid: false, error: "Password is required" };
  if (password.length < 8) return { valid: false, error: "Password must be at least 8 characters long" };
 
  if (!/[A-Z]/.test(password)) return { valid: false, error: "Password must contain at least one uppercase letter" };
  if (!/[0-9]/.test(password)) return { valid: false, error: "Password must contain at least one number" };
  if (!/[!@#$%^&*]/.test(password)) return { valid: false, error: "Password must contain at least one special character (!@#$%^&*)" };
  return { valid: true, error: null };
};
  
  setBulkLoading(true);
  setUploadProgress(0);
  
  try {
    const text = await csvFile.text();
    const filteredText = text
      .split(/\r?\n/)
      .filter((line) => line.trim() && !line.trim().startsWith("#"))
      .join("\n");

    const parsed = Papa.parse(filteredText, { header: true });
    
    const validationErrors = [];
    const fixedRows = (parsed.data || [])
      .filter((row) => row.username)
      .map((row, index) => {
        let deskIds = [];
        const deskIdRaw = row.desk_id || row.desk_ids || "";
        if (deskIdRaw) {
          deskIds = deskIdRaw.toString().split(',')
            .map(id => parseInt(id.trim()))
            .filter(id => !isNaN(id));
        }
        
        // Get password (use default if not provided)
        const password = row.password || "default123";
        
        // Validate password
        const passwordValidation = validatePassword(password, index + 1);
        if (!passwordValidation.valid) {
          validationErrors.push(`Row ${index + 1} (${row.username}): ${passwordValidation.error}`);
        }
        
        // Validate role_id
        const roleId = parseInt(row.role_id || row.role);
        if (isNaN(roleId)) {
          validationErrors.push(`Row ${index + 1} (${row.username}): Invalid or missing role_id`);
        }
        
        // For DMA roles, validate desk_ids
        const isDMARole = DMA_ROLES.includes(roleId);
        if (isDMARole && deskIds.length === 0) {
          validationErrors.push(`Row ${index + 1} (${row.username}): DMA role requires at least one desk assignment`);
        }

        return {
          row_index: index + 1,
          username: String(row.username || "").trim(),
          full_name: row.full_name || row.username,
          name: row.name || row.full_name || row.username,
          email: (row.email || "").trim().toLowerCase() || null,
          password: password, // Original password (validation done, backend will also validate)
          role_id: roleId,
          designation: row.designation?.trim() || "",
          desk_ids: deskIds,
          state_name: row.state_name || row.state || "",
          division_name: row.division_name || row.division || "",
          district_name: row.district_name || row.district || "",
          ulb_name: row.ulb_name || "",
          ulb_type: row.ulb_type || "",
          has_form_builder_access: row.has_form_builder_access === "true" || row.has_form_builder_access === "1" || false,
          status: row.status || "active",
          isValid: !passwordValidation.error && !isNaN(roleId) && (!isDMARole || deskIds.length > 0)
        };
      });
    
    // Filter valid rows only
    const validRows = fixedRows.filter(row => row.isValid === true);
    const invalidRows = fixedRows.filter(row => row.isValid !== true);
    
    // Show validation summary
    if (validationErrors.length > 0) {
      const errorSummary = validationErrors.slice(0, 10).join('\n');
      const continueUpload = window.confirm(
        `⚠️ Validation Failed:\n\n` +
        `Total rows: ${fixedRows.length}\n` +
        `Valid rows: ${validRows.length}\n` +
        `Invalid rows: ${invalidRows.length}\n\n` +
        `Errors (first 10):\n${errorSummary}\n\n` +
        `${invalidRows.length > 10 ? `... and ${invalidRows.length - 10} more errors\n\n` : ''}` +
        `Do you want to continue uploading only the ${validRows.length} valid rows?`
      );
      
      if (!continueUpload) {
        setBulkLoading(false);
        return;
      }
    }
    
    if (validRows.length === 0) {
      alert("No valid users found in CSV. Please check:\n- Password (min 8 chars, uppercase, number, special char)\n- Role ID\n- Desk assignments for DMA roles");
      setBulkLoading(false);
      return;
    }
    
    console.log("📤 Bulk upload users with desk assignments:", validRows.filter(r => r.desk_ids?.length > 0).length);
    console.log("📤 Total valid users to upload:", validRows.length);

    const response = await axios.post(`${API_BASE}/admin/bulk-create-users`, { users: validRows }, {
      timeout: 60000,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      }
    });

    const summary = {
      success_count: response.data.success_count || 0,
      failed_count: response.data.failed_count || 0,
      errors: [...validationErrors, ...(response.data.errors || [])],
      skipped_count: invalidRows.length
    };

    setUploadSummary(summary);
    
    let message = `${summary.success_count} users created successfully!`;
    if (summary.skipped_count > 0) {
      message += `\n${summary.skipped_count} rows were skipped due to validation errors.`;
    }
    if (summary.failed_count > 0) {
      message += `\n${summary.failed_count} users failed on server.`;
    }
    alert(message);
    
    setCsvFile(null);

    setTimeout(() => {
      setUploadSummary(null);
      fetchUsers();
    }, 2000);

  } catch (error) {
    console.error("Bulk upload ERROR:", error);
    const errorMsg = error.response?.data?.error || error.message;
    alert(`Bulk upload failed: ${errorMsg}\n\nPlease check:\n- Password format (min 8 chars, uppercase, number, special char)\n- All required fields are present`);
  } finally {
    setBulkLoading(false);
    setUploadProgress(0);
  }
};

  // ========== CSV TEMPLATES ==========
const roleTemplates = {
  1: () => `# ULB USERS CSV TEMPLATE (role_id=1)
username,full_name,name,email,password,role_id,designation,has_form_builder_access,state_name,division_name,district_name,ulb_name,status
bmc_test,"BMC Admin","BMC Admin",,Bmc@1234,1,"ULB User",true,"Maharashtra","Pune","Pune","Brihanmumbai MC","active"`,

  2: () => `# DISTRICT ADMIN CSV TEMPLATE (role_id=2)
username,full_name,name,email,password,role_id,designation,has_form_builder_access,state_name,division_name,district_name,status
pune_test,"Pune District Admin","Pune District Admin",,Pune789@,2,"District Magistrate",false,"Maharashtra","Pune","Pune","active"`,

  3: () => `# DIVISIONAL OFFICER CSV TEMPLATE (role_id=3)
username,full_name,name,email,password,role_id,designation,has_form_builder_access,state_name,division_name,status
pune_div_test,"Pune Div Officer","Pune Div Officer",,Puned45@,3,"Divisional Commissioner",true,"Maharashtra","Pune","active"`,

  4: () => `# DMA DESK OFFICER CSV TEMPLATE (role_id=4) - Multiple desks supported
username,full_name,name,email,password,role_id,designation,desk_ids,has_form_builder_access,state_name,status
dma_desk1_test,"DMA Desk Officer (Multiple)","DMA Desk Officer",,Dma123@,4,"Desk Officer","1,2,3",true,"Maharashtra","active"
dma_desk2_test,"DMA Desk Officer (Single)","DMA Desk Officer",,Dma123@,4,"Desk Officer","4",false,"Maharashtra","active"`,

  5: () => `# SUPPORT ADMIN CSV TEMPLATE (role_id=5)
username,full_name,name,email,password,role_id,designation,has_form_builder_access,status
support_admin,"Support Admin","Support Admin",,Support123@,5,"Support Administrator",true,"active"`,

  6: () => `# DMA DESK USER CSV TEMPLATE (role_id=6) - Multiple desks supported
username,full_name,name,email,password,role_id,designation,desk_ids,has_form_builder_access,state_name,status
dma_user1_test,"DMA Desk User (Multiple)","DMA Desk User",,Dma123@,6,"Desk User","5,6,7",false,"Maharashtra","active"
dma_user2_test,"DMA Desk User (Single)","DMA Desk User",,Dma123@,6,"Desk User","8",false,"Maharashtra","active"`,

  7: () => `# ULB CHIEF OFFICER CSV TEMPLATE (role_id=7)
username,full_name,name,email,password,role_id,designation,has_form_builder_access,state_name,division_name,district_name,ulb_name,status
chief_officer_test,"ULB Chief Officer","ULB Chief Officer",,Chief123@,7,"Chief Officer",true,"Maharashtra","Pune","Pune","Pune Municipal Corporation","active"`,

  8: () => `# DMA ADMIN CSV TEMPLATE (role_id=8)
username,full_name,name,email,password,role_id,designation,has_form_builder_access,state_name,status
dma_admin_test,"DMA Admin","DMA Admin",,DmaAdmin123@,8,"DMA Administrator",true,"Maharashtra","active"`,

  9: () => `# DMA SYSTEM ADMIN CSV TEMPLATE (role_id=9)
username,full_name,name,email,password,role_id,designation,has_form_builder_access,state_name,status
dma_sysadmin_test,"DMA System Admin","DMA System Admin",,DmaSys123@,9,"System Administrator",true,"Maharashtra","active"`,

  10: () => `# SUPPORT USER CSV TEMPLATE (role_id=10)
username,full_name,name,email,password,role_id,designation,has_form_builder_access,state_name,status
support_user_test,"Support User","Support User",,SupportUser123@,10,"Support User",false,"Maharashtra","active"`
};

  // ========== DOWNLOAD TEMPLATE ==========
  const downloadTemplate = useCallback((selectedRole = null) => {
    let csvContent;
    if (selectedRole && roleTemplates[selectedRole]) {
      csvContent = roleTemplates[selectedRole]();
    } else {
      csvContent = Object.values(roleTemplates).join('\n\n---\n\n');
    }

    const filename = selectedRole
      ? `dma_${roleOptions.find(r => r.value === selectedRole)?.label?.toLowerCase().replace(/\s+/g, '_')}_template.csv`
      : "dma_all_roles_template.csv";

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [roleOptions]);

  // ========== EFFECTS ==========
  useEffect(() => {
    fetchRoles();
    fetchDesks();
    fetchStates();
    fetchUsers();
  }, [fetchRoles, fetchDesks, fetchStates, fetchUsers]);

  useEffect(() => {
    try { sessionStorage.setItem(LS_KEY, String(sbCollapsed)); } catch { }
  }, [sbCollapsed]);

  // ========== VALIDATION ==========
  // NEW CODE - 8 किंवा त्यापेक्षा जास्त characters
const validatePassword = (password) => {
  if (!password) return "Password is required";
  if (password.length < 8) return "Password must be at least 8 characters long";
  // No upper limit check
  if (!/[A-Z]/.test(password)) return "At least one uppercase letter required";
  if (!/[0-9]/.test(password)) return "At least one number required";
  if (!/[!@#$%^&*]/.test(password)) return "At least one special character required";
  return "";
};

  // ========== HANDLE FORM CHANGES ==========
  // ========== HANDLE FORM CHANGES ==========
const handleFormChange = (field, value) => {
  const processedValue = field === "has_form_builder_access" ? Boolean(value) : value;

  setFormData(prev => ({ ...prev, [field]: processedValue }));

  if (field === "role") {
    // Reset desk assignments when role changes
    setFormData(prev => ({ ...prev, desk_ids: [], desk_names: [] }));

    if (value === "5") {
      setFormData(prev => ({
        ...prev,
        state: "",
        division: "",
        district: "",
        ulb_id: "",
        ulb_name: ""
      }));
      setSelectedState("");
      setSelectedDivision("");
      setSelectedDistrict("");
      setSelectedUlb("");
    }
  } else if (field === "state" && value) {
    fetchDivisions(value);
    setSelectedState(value);
    setSelectedDivision("");
    setSelectedDistrict("");
    setSelectedUlb("");
    setFormData(prev => ({ ...prev, division: "", district: "", ulb_id: "", ulb_name: "" }));
  } else if (field === "division" && value) {
    fetchDistricts(value);
    setSelectedDivision(value);
    setSelectedDistrict("");
    setSelectedUlb("");
    setFormData(prev => ({ ...prev, district: "", ulb_id: "", ulb_name: "" }));
  } else if (field === "district" && value) {
    setSelectedDistrict(value);
    if (formData.role === "1" || formData.role === "7") {
      fetchUlbs(value);
    }
    setFormData(prev => ({ ...prev, ulb_id: "", ulb_name: "" }));
  } else if (field === "ulb_id" && value) {
    // FIX: Define variables before using them
    const ulbIdNum = parseInt(value);
    setSelectedUlb(ulbIdNum);
    const ulbDetails = ulbOptions.find(u => u.id === ulbIdNum);
    const ulbName = ulbDetails?.ulb_name || "";
    const ulbType = ulbDetails?.ulb_type || "";
    
    console.log("📌 ULB Selected:", { ulbIdNum, ulbName, ulbType });
    
    setFormData(prev => ({ 
      ...prev, 
      ulb_id: ulbIdNum,      // Number store करा
      ulb_name: ulbName,
      ulb_type: ulbType
    }));
  }
};

  // ========== CREATE SINGLE USER ==========
const onSingleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  try {
    // Debug logging to see what's being submitted
    console.log("🔍 FORM DATA BEFORE SUBMIT:", {
      role: formData.role,
      selectedUlb: selectedUlb,
      formData_ulb_id: formData.ulb_id,
      formData_ulb_name: formData.ulb_name,
      ulbOptions_length: ulbOptions.length
    });

    // Fix ULB ID for role 1 (ULB Users) and role 7 (ULB Chief Officer)
    let finalUlbId = formData.ulb_id;
    let finalUlbName = formData.ulb_name;
    
    if ((formData.role === "1" || formData.role === "7") && selectedUlb) {
      // Ensure ulb_id is a proper number
      finalUlbId = parseInt(selectedUlb);
      
      // Get ULB name from options if not already set
      if (!finalUlbName) {
        const selectedUlbObj = ulbOptions.find(u => u.id === finalUlbId);
        finalUlbName = selectedUlbObj?.ulb_name || "";
      }
      
      console.log("✅ Fixed ULB values:", { finalUlbId, finalUlbName });
    }

    const payload = {
      username: formData.username,
      full_name: formData.full_name,
      name: formData.full_name,
      email: formData.email && formData.email.trim() !== "" ? formData.email : null,
      password: formData.password,
      role_id: parseInt(formData.role), // Ensure it's a number
      designation: formData.designation?.trim() || null,
      desk_ids: DMA_ROLES.includes(parseInt(formData.role)) ? formData.desk_ids : [],
      state_id: formData.state ? parseInt(formData.state) : null,
      division_id: formData.division ? parseInt(formData.division) : null,
      district_id: formData.district ? parseInt(formData.district) : null,
      ulb_id: finalUlbId, // Use fixed ULB ID
      ulb_name: finalUlbName, // Use fixed ULB name
      ulb_type: formData.ulb_type || null,
      has_form_builder_access: formData.has_form_builder_access || false,
      status: formData.status || "active"
    };

    console.log("📤 FINAL PAYLOAD SENT TO BACKEND:", payload);
    console.log("📤 ULB ID type:", typeof payload.ulb_id, "Value:", payload.ulb_id);
    console.log("📤 ULB Name:", payload.ulb_name);

    await axios.post(`${API_BASE}/admin/create-user`, payload, {
      headers: { "Content-Type": "application/json" },
      timeout: 30000,
    });

    alert("User created successfully!");
    
    // Reset form
    setFormData({
      username: "",
      full_name: "",
      email: "",
      password: "",
      role: "",
      designation: "",
      desk_ids: [],
      desk_names: [],
      state: "",
      division: "",
      district: "",
      ulb_id: "",
      ulb_name: "",
      ulb_type: "",
      has_form_builder_access: false,
      status: "active"
    });
    setSelectedState("");
    setSelectedDivision("");
    setSelectedDistrict("");
    setSelectedUlb("");
    setUlbOptions([]);
    fetchUsers();
    
  } catch (error) {
    console.error("Create user error:", error);
    console.error("Error response:", error.response?.data);
    alert(error.response?.data?.error || "Failed to create user");
  } finally {
    setLoading(false);
  }
};

  // ========== DELETE USER ==========
  const deleteUser = async (username) => {
    if (window.confirm(`Delete user "${username}"?`)) {
      try {
        await axios.delete(`${API_BASE}/admin/delete-user?username=${encodeURIComponent(username)}`);
        alert("User deleted successfully");
        fetchUsers();
      } catch (err) {
        alert("Delete failed: " + (err.response?.data?.error || err.message));
      }
    }
  };

  // ========== UPDATE USER STATUS ==========
  const updateUserStatus = async (username, newStatus) => {
    try {
      await axios.put(`${API_BASE}/admin/update-user-status`, {
        username,
        status: newStatus
      });
      alert(`User status updated to ${newStatus}`);
      fetchUsers();
    } catch (err) {
      alert("Status update failed: " + (err.response?.data?.error || err.message));
    }
  };

  // ========== ROLE STATISTICS ==========
  const roleStats = useMemo(() => {
    const stats = {};
    roleOptions.forEach(role => {
      stats[role.value] = usersList.filter(user => parseInt(user.role) === role.value).length;
    });
    return stats;
  }, [usersList, roleOptions]);

  // Roles that can be created (excludes Cader and Visitor)
  const creatableRoles = useMemo(() => {
    return roleOptions.filter(role => {
      const roleNameLower = role.label.toLowerCase();
      // Exclude roles that contain 'cader' or 'visitor'
      return !roleNameLower.includes('cader') &&
        !roleNameLower.includes('visitor');
    });
  }, [roleOptions]);

  // ========== CLEAR FILTERS ==========
  const clearFilters = () => {
    setSearch("");
    setRoleFilter("");
    setShowMobileFilters(false);
    setShowMobileSearch(false);
  };

  // ========== RESPONSIVE STYLES ==========
  // const contentMarginLeft = isMobile ? 0 : baseMargin;
  // const contentWidth = isMobile ? "100%" : `calc(100% - ${baseMargin}px)`;

  return (
    <>
      <Navbar onMenuClick={() => { }} /> {/* Empty function since sidebar removed */}

      {/* MAIN CONTENT - FULL WIDTH */}
      <div style={{
        minHeight: "100vh",
        background: "#f8fafc",
        paddingTop: "85px", // Space for navbar
        paddingLeft: "20px",
        paddingRight: "20px",
        paddingBottom: "20px",
        width: "100%",
        boxSizing: "border-box"
      }}>
        <div className="main-content-container" style={{
          padding: isMobile ? 12 : 20,
          background: "#fff",
          borderRadius: 8,
          minHeight: "calc(100vh - 85px - 32px)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          margin: 0,
          position: "relative",
          zIndex: 1 // Ensure content is below navbar but above other elements
        }}>

          {/* Header with separating line - UPDATED with proper positioning */}
          <div style={{ marginBottom: 24, position: "relative", zIndex: 1 }}>
            <div style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              gap: isMobile ? 12 : 0,
              marginBottom: 12
            }}>
              <div className="header-content">
                <h1 style={{
                  margin: 0,
                  color: "#000000",
                  fontSize: isMobile ? 20 : 24,
                  fontWeight: 600
                }}>
                  <UserAddOutlined style={{ marginRight: 8, color: "#113F67" }} />
                  User Management
                </h1>
              </div>

              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                {/* Pending Approvals Button */}
                {/* <button
                  onClick={() => navigate('/pending-approvals')}
                  style={{
                    background: "linear-gradient(45deg, #ff9800, #f57c00)",
                    color: "white",
                    border: "none",
                    padding: isMobile ? "10px 16px" : "12px 24px",
                    borderRadius: "8px",
                    fontSize: isMobile ? 14 : 16,
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 4px 10px rgba(255, 152, 0, 0.3)",
                    transition: "all 0.3s ease"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
                  onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
                >
                  <TeamOutlined />
                  Pending Approvals
                </button> */}

                {/* Visitors History Button */}
                <button
                  onClick={() => navigate('/admin/visitors')}
                  style={{
                    background: "linear-gradient(45deg, #17a2b8, #138496)",
                    color: "white",
                    border: "none",
                    padding: isMobile ? "10px 16px" : "12px 24px",
                    borderRadius: "8px",
                    fontSize: isMobile ? 14 : 16,
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 4px 10px rgba(23, 162, 184, 0.3)",
                    transition: "all 0.3s ease"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
                  onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
                >
                  <HistoryOutlined />
                  View Visitors
                </button>
              </div>
            </div>

            {/* Thin gray separating line */}
            <div style={{
              height: '1px',
              backgroundColor: '#e8e8e8',
              width: '100%',
              marginTop: '4px'
            }} />
          </div>

          {/* Mobile Search & Filter Toggle */}
          {isMobile && activeTab === "view" && (
            <div className="mobile-controls" style={{
              display: "flex",
              gap: 8,
              marginBottom: 16
            }}>
              <button
                onClick={() => setShowMobileSearch(!showMobileSearch)}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  background: "#f8f9fa",
                  border: "1px solid #ced4da",
                  borderRadius: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  fontSize: 14,
                  cursor: "pointer"
                }}
              >
                <SearchOutlined /> {showMobileSearch ? "Hide Search" : "Search"}
              </button>
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  background: "#f8f9fa",
                  border: "1px solid #ced4da",
                  borderRadius: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  fontSize: 14,
                  cursor: "pointer"
                }}
              >
                <FilterOutlined /> {showMobileFilters ? "Hide Filters" : "Filters"}
              </button>
            </div>
          )}

          {/* Mobile Search Panel */}
          {isMobile && activeTab === "view" && showMobileSearch && (
            <div className="mobile-search-panel" style={{
              padding: 16,
              background: "#f8f9fa",
              borderRadius: 8,
              marginBottom: 16,
              border: "1px solid #e9ecef"
            }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{
                  display: "block",
                  marginBottom: 8,
                  fontWeight: 500,
                  fontSize: 14
                }}>
                  Search Users
                </label>
                <input
                  type="text"
                  placeholder="Search by name, username, email, designation, ULB, or desk..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ced4da",
                    borderRadius: 6,
                    fontSize: 14
                  }}
                />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => setShowMobileSearch(false)}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background: "#6c757d",
                    color: "white",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer"
                  }}
                >
                  Close
                </button>
                <button
                  onClick={fetchUsers}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background: "#113F67",
                    color: "white",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer"
                  }}
                >
                  Search
                </button>
              </div>
            </div>
          )}

          {/* Mobile Filter Panel */}
          {isMobile && activeTab === "view" && showMobileFilters && (
            <div className="mobile-filter-panel" style={{
              padding: 16,
              background: "#f8f9fa",
              borderRadius: 8,
              marginBottom: 16,
              border: "1px solid #e9ecef"
            }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{
                  display: "block",
                  marginBottom: 8,
                  fontWeight: 500,
                  fontSize: 14
                }}>
                  Filter by Role
                </label>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ced4da",
                    borderRadius: 6,
                    fontSize: 14,
                    background: "white"
                  }}
                >
                  <option value="">All Roles</option>
                  {roleOptions.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={clearFilters}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background: "#e74c3c",
                    color: "white",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontSize: 14
                  }}
                >
                  Clear Filters
                </button>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background: "#6c757d",
                    color: "white",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontSize: 14
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="tabs-container" style={{
            background: "white",
            borderRadius: 12,
            boxShadow: isMobile ? "none" : "0 2px 8px rgba(0,0,0,0.1)",
            overflow: "hidden",
            marginBottom: 24,
            border: isMobile ? "1px solid #e9ecef" : "none"
          }}>
            <div className="tabs-header" style={{
              display: "flex",
              background: "#f8f9fa",
              borderBottom: "2px solid #e9ecef",
              padding: 0,
              overflowX: isMobile ? "auto" : "visible",
              whiteSpace: "nowrap",
              scrollbarWidth: "none",
              msOverflowStyle: "none"
            }}>
              <button
                className={`tab-btn ${activeTab === "single" ? "active" : ""}`}
                onClick={() => setActiveTab("single")}
                style={{
                  flex: isMobile ? "0 0 auto" : 1,
                  padding: isMobile ? "12px 16px" : "16px 24px",
                  background: "none",
                  border: "none",
                  fontSize: isMobile ? 14 : 16,
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.3s",
                  position: "relative",
                  borderRadius: "8px 8px 0 0",
                  minWidth: isMobile ? "100px" : "auto",
                  color: activeTab === "single" ? "white" : "#6c757d",
                  background: activeTab === "single" ? "#113F67" : "transparent"
                }}
              >
                <UserOutlined style={{ marginRight: 8 }} />
                {isMobile ? "Single" : "Single User"}
              </button>
              <button
                className={`tab-btn ${activeTab === "bulk" ? "active" : ""}`}
                onClick={() => setActiveTab("bulk")}
                style={{
                  flex: isMobile ? "0 0 auto" : 1,
                  padding: isMobile ? "12px 16px" : "16px 24px",
                  background: "none",
                  border: "none",
                  fontSize: isMobile ? 14 : 16,
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.3s",
                  position: "relative",
                  borderRadius: "8px 8px 0 0",
                  minWidth: isMobile ? "100px" : "auto",
                  color: activeTab === "bulk" ? "white" : "#6c757d",
                  background: activeTab === "bulk" ? "#113F67" : "transparent"
                }}
              >
                <UploadOutlined style={{ marginRight: 8 }} />
                {isMobile ? "Bulk" : "Bulk Upload"}
              </button>
              <button
                className={`tab-btn ${activeTab === "view" ? "active" : ""}`}
                onClick={() => setActiveTab("view")}
                style={{
                  flex: isMobile ? "0 0 auto" : 1,
                  padding: isMobile ? "12px 16px" : "16px 24px",
                  background: "none",
                  border: "none",
                  fontSize: isMobile ? 14 : 16,
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.3s",
                  position: "relative",
                  borderRadius: "8px 8px 0 0",
                  minWidth: isMobile ? "100px" : "auto",
                  color: activeTab === "view" ? "white" : "#6c757d",
                  background: activeTab === "view" ? "#113F67" : "transparent"
                }}
              >
                <EyeOutlined style={{ marginRight: 8 }} />
                {isMobile ? "View" : "View Users"}
                {!isMobile && filteredUsers.length > 0 && (
                  <span style={{
                    background: "#e74c3c",
                    color: "white",
                    padding: "2px 8px",
                    borderRadius: 10,
                    fontSize: 12,
                    marginLeft: 8
                  }}>
                    {filteredUsers.length}
                  </span>
                )}
              </button>
            </div>

            <div className="tab-content" style={{
              padding: isMobile ? 16 : 24,
              maxWidth: "100%",
              overflow: "hidden"
            }}>
              {/* ========== SINGLE USER TAB ========== */}
              {activeTab === "single" && (
                <div className="single-user-tab">
                  <div className="alert alert-info" style={{
                    padding: isMobile ? 12 : 16,
                    borderRadius: 8,
                    marginBottom: 24,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    background: "#d1ecf1",
                    border: "1px solid #bee5eb",
                    color: "#0c5460",
                    fontSize: isMobile ? 13 : 14
                  }}>
                    <InfoCircleOutlined />
                    <span>Fill in the details below to create a single user account</span>
                  </div>

                  <form onSubmit={onSingleSubmit}>
                    {/* Basic Information Section */}
                    <div className="form-section" style={{
                      marginBottom: 24,
                      padding: isMobile ? 16 : 20,
                      background: "#f8f9fa",
                      borderRadius: 8,
                      border: "1px solid #e9ecef"
                    }}>
                      <h4 style={{
                        margin: "0 0 16px 0",
                        color: "#000000",
                        borderBottom: "2px solid #113F67",
                        paddingBottom: 8,
                        fontSize: isMobile ? 16 : 18,
                        fontWeight: 600
                      }}>
                        <IdcardOutlined style={{ marginRight: 8, color: "#113F67" }} />
                        Basic Information
                      </h4>
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(250px, 1fr))",
                        gap: isMobile ? 12 : 20
                      }}>
                        <div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
                          <label style={{
                            marginBottom: 8,
                            fontWeight: 500,
                            color: "#495057",
                            fontSize: isMobile ? 14 : 16
                          }}>Username *</label>
                          <input
                            type="text"
                            value={formData.username}
                            onChange={(e) => handleFormChange("username", e.target.value)}
                            placeholder="e.g., john.doe"
                            required
                            style={{
                              padding: isMobile ? 10 : 12,
                              border: "1px solid #ced4da",
                              borderRadius: 6,
                              fontSize: isMobile ? 14 : 16
                            }}
                          />
                        </div>
                        <div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
                          <label style={{
                            marginBottom: 8,
                            fontWeight: 500,
                            color: "#495057",
                            fontSize: isMobile ? 14 : 16
                          }}>Full Name *</label>
                          <input
                            type="text"
                            value={formData.full_name}
                            onChange={(e) => handleFormChange("full_name", e.target.value)}
                            placeholder="e.g., John Doe"
                            required
                            style={{
                              padding: isMobile ? 10 : 12,
                              border: "1px solid #ced4da",
                              borderRadius: 6,
                              fontSize: isMobile ? 14 : 16
                            }}
                          />
                        </div>
                      {/* In the Basic Information section */}
<div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
  <label style={{
    marginBottom: 8,
    fontWeight: 500,
    color: "#495057",
    fontSize: isMobile ? 14 : 16
  }}>
    Email <span style={{ color: "#6c757d", fontSize: 12, fontWeight: "normal" }}>(Optional)</span>
  </label>
  <input
    type="email"
    value={formData.email}
    onChange={(e) => handleFormChange("email", e.target.value)}
    placeholder="user@dma.gov.in (optional)"
    style={{
      padding: isMobile ? 10 : 12,
      border: "1px solid #ced4da",
      borderRadius: 6,
      fontSize: isMobile ? 14 : 16
    }}
  />
</div>
                        <div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
                          <label style={{
                            marginBottom: 8,
                            fontWeight: 500,
                            color: "#495057",
                            fontSize: isMobile ? 14 : 16
                          }}>
                            Password *
                          </label>
                          <input
                            type="password"
                            value={formData.password}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleFormChange("password", value);
                              setPasswordError(validatePassword(value));
                            }}
                            placeholder="e.g, Admin@123"
                            required
                            style={{
                              padding: isMobile ? 10 : 12,
                              border: passwordError ? "1px solid #dc3545" : "1px solid #ced4da",
                              borderRadius: 6,
                              fontSize: isMobile ? 14 : 16,
                            }}
                          />
                          {passwordError && (
                            <span style={{
                              color: "#dc3545",
                              fontSize: isMobile ? 12 : 13,
                              marginTop: 6
                            }}>
                              <WarningOutlined style={{ marginRight: 4 }} />
                              {passwordError}
                            </span>
                          )}
                        </div>

                        {/* Status Selection */}
                        <div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
                          <label style={{
                            marginBottom: 8,
                            fontWeight: 500,
                            color: "#495057",
                            fontSize: isMobile ? 14 : 16
                          }}>Status</label>
                          <select
                            value={formData.status}
                            onChange={(e) => handleFormChange("status", e.target.value)}
                            style={{
                              padding: isMobile ? 10 : 12,
                              border: "1px solid #ced4da",
                              borderRadius: 6,
                              fontSize: isMobile ? 14 : 16
                            }}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="suspended">Suspended</option>
                          </select>
                        </div>

                        {/* Form Builder Permission Checkbox */}
                        <div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
                          <label style={{
                            marginBottom: 8,
                            fontWeight: 500,
                            color: "#495057",
                            display: "flex",
                            alignItems: "center",
                            fontSize: isMobile ? 14 : 16
                          }}>
                            <input
                              type="checkbox"
                              checked={formData.has_form_builder_access}
                              onChange={(e) => handleFormChange("has_form_builder_access", e.target.checked)}
                              style={{
                                marginRight: 8,
                                transform: "scale(1.2)"
                              }}
                            />
                            Grant Form Builder Access
                          </label>
                          <small style={{
                            color: "#6c757d",
                            fontSize: isMobile ? 12 : 13
                          }}>
                            Allows user to create and manage forms in the Form Builder module
                          </small>
                        </div>
                      </div>
                    </div>

                    {/* Role & Permissions Section */}
                    <div className="form-section" style={{
                      marginBottom: 24,
                      padding: isMobile ? 16 : 20,
                      background: "#f8f9fa",
                      borderRadius: 8,
                      border: "1px solid #e9ecef"
                    }}>
                      <h4 style={{
                        margin: "0 0 16px 0",
                        color: "#000000",
                        borderBottom: "2px solid #113F67",
                        paddingBottom: 8,
                        fontSize: isMobile ? 16 : 18,
                        fontWeight: 600
                      }}>
                        <TeamOutlined style={{ marginRight: 8, color: "#113F67" }} />
                        Role & Permissions
                      </h4>
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(250px, 1fr))",
                        gap: isMobile ? 12 : 20
                      }}>
                        <div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
                          <label style={{
                            marginBottom: 8,
                            fontWeight: 500,
                            color: "#495057",
                            fontSize: isMobile ? 14 : 16
                          }}>Select Role *</label>
                          <select
                            value={formData.role}
                            onChange={(e) => handleFormChange("role", e.target.value)}
                            required
                            disabled={loadingRoles}
                            style={{
                              padding: isMobile ? 10 : 12,
                              border: "1px solid #ced4da",
                              borderRadius: 6,
                              fontSize: isMobile ? 14 : 16
                            }}
                          >
                            <option value="">Choose a role</option>
                            {creatableRoles.map((r) => (  // <-- USE creatableRoles INSTEAD
                              <option key={r.value} value={r.value}>
                                {r.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {formData.role && (
                          <div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
                            <label style={{
                              marginBottom: 8,
                              fontWeight: 500,
                              color: "#495057",
                              fontSize: isMobile ? 14 : 16
                            }}>
                              Designation
                              <span style={{
                                fontSize: isMobile ? 11 : 12,
                                color: "#6c757d",
                                marginLeft: 8,
                                fontWeight: "normal"
                              }}>
                                (Optional)
                              </span>
                            </label>
                            <input
                              type="text"
                              value={formData.designation}
                              onChange={(e) => handleFormChange("designation", e.target.value)}
                              placeholder="e.g., Chief Officer, District Magistrate, Commissioner, etc."
                              style={{
                                padding: isMobile ? 10 : 12,
                                border: "1px solid #ced4da",
                                borderRadius: 6,
                                fontSize: isMobile ? 14 : 16
                              }}
                            />
                          </div>
                        )}
                      </div>

                      {/* MULTIPLE DESK ASSIGNMENT SECTION - For DMA roles */}
                      {formData.role && DMA_ROLES.includes(parseInt(formData.role)) && (
                        <div className="desk-assignment-section" style={{
                          marginTop: 20,
                          padding: isMobile ? 12 : 16,
                          background: "#fff",
                          borderRadius: 8,
                          border: "1px solid #e9ecef"
                        }}>
                          <h5 style={{
                            margin: "0 0 12px 0",
                            color: "#000000",
                            fontSize: isMobile ? 15 : 16,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontWeight: 500
                          }}>
                            <DesktopOutlined style={{ color: "#113F67" }} />
                            Desk Assignment
                            <span style={{
                              fontSize: isMobile ? 11 : 12,
                              color: "#e74c3c",
                              marginLeft: 8,
                              fontWeight: "normal"
                            }}>
                              (Select one or multiple desks)
                            </span>
                          </h5>

                          {loadingDesks ? (
                            <div style={{ textAlign: "center", padding: 20 }}>
                              Loading desks...
                            </div>
                          ) : (
                            <>
                              <div style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: isMobile ? 8 : 12,
                                marginTop: 12,
                                alignItems: "center"
                              }}>
                                {deskOptions.map((desk) => {
                                  const isSelected = formData.desk_ids.includes(desk.id);
                                  return (
                                    <div
                                      key={desk.id}
                                      onClick={() => handleDeskSelection(desk.id)}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        padding: "8px 12px",
                                        background: isSelected ? "#f0f7ff" : "transparent",
                                        border: isSelected ? `2px solid ${desk.color}` : "2px solid #e9ecef",
                                        borderRadius: 8,
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                        fontSize: isMobile ? 12 : 13,
                                        fontWeight: isSelected ? 600 : 400,
                                        color: "#495057",
                                        minWidth: isMobile ? "calc(50% - 8px)" : "auto",
                                        flex: isMobile ? "0 0 auto" : "none"
                                      }}
                                    >
                                      <div style={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: 4,
                                        border: `2px solid ${isSelected ? desk.color : "#ced4da"}`,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: isSelected ? desk.color : "white"
                                      }}>
                                        {isSelected && (
                                          <CheckOutlined style={{ color: "white", fontSize: 12 }} />
                                        )}
                                      </div>
                                      <span>{desk.name}</span>
                                    </div>
                                  );
                                })}
                              </div>

                              {formData.desk_ids.length > 0 && (
                                <div style={{
                                  marginTop: 16,
                                  padding: 12,
                                  background: "#e8f5e9",
                                  borderRadius: 6,
                                  fontSize: isMobile ? 12 : 13,
                                  color: "#2e7d32"
                                }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                    <CheckOutlined />
                                    <span>Selected Desks:</span>
                                  </div>
                                  <div style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 8,
                                    marginTop: 4
                                  }}>
                                    {formData.desk_ids.map(id => {
                                      const desk = deskOptions.find(d => d.id === id);
                                      return (
                                        <span
                                          key={id}
                                          style={{
                                            display: "inline-block",
                                            padding: "4px 10px",
                                            background: "white",
                                            borderRadius: 16,
                                            border: `1px solid ${desk?.color || "#000"}`,
                                            fontSize: isMobile ? 11 : 12,
                                            fontWeight: 500
                                          }}
                                        >
                                          {desk?.name || `Desk ${id}`}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              <small style={{
                                color: "#6c757d",
                                fontSize: isMobile ? 11 : 12,
                                display: "block",
                                marginTop: 12
                              }}>
                                <InfoCircleOutlined style={{ marginRight: 4 }} />
                                Users can be assigned to multiple desks. Each desk determines their workflow access.
                              </small>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Geographic Details Section */}
                   
{formData.role && formData.role !== "5" && !DMA_ROLES.includes(parseInt(formData.role)) && (
  <div className="form-section" style={{
    marginBottom: 24,
    padding: isMobile ? 16 : 20,
    background: "#f8f9fa",
    borderRadius: 8,
    border: "1px solid #e9ecef"
  }}>
    <h4 style={{
      margin: "0 0 16px 0",
      color: "#000000",
      borderBottom: "2px solid #113F67",
      paddingBottom: 8,
      fontSize: isMobile ? 16 : 18,
      fontWeight: 600
    }}>
      <EnvironmentOutlined style={{ marginRight: 8, color: "#113F67" }} />
      Geographic Details
    </h4>
    <div style={{
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(250px, 1fr))",
      gap: isMobile ? 12 : 20
    }}>
      {/* State - Always required */}
      <div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
        <label style={{
          marginBottom: 8,
          fontWeight: 500,
          color: "#495057",
          fontSize: isMobile ? 14 : 16
        }}>State *</label>
        <select
          value={selectedState}
          onChange={(e) => handleFormChange("state", e.target.value)}
          required
          style={{
            padding: isMobile ? 10 : 12,
            border: "1px solid #ced4da",
            borderRadius: 6,
            fontSize: isMobile ? 14 : 16
          }}
        >
          <option value="">Select State</option>
          {stateOptions.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      {/* Division - Always required for non-DMA roles except support admin */}
      <div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
        <label style={{
          marginBottom: 8,
          fontWeight: 500,
          color: "#495057",
          fontSize: isMobile ? 14 : 16
        }}>Division *</label>
        <select
          value={selectedDivision}
          onChange={(e) => handleFormChange("division", e.target.value)}
          disabled={!selectedState}
          required
          style={{
            padding: isMobile ? 10 : 12,
            border: "1px solid #ced4da",
            borderRadius: 6,
            fontSize: isMobile ? 14 : 16
          }}
        >
          <option value="">Select Division</option>
          {divisionOptions.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
      </div>

      {/* District - Only for roles that need district (role_id: 1, 2, 7) */}
      {formData.role === "1" || formData.role === "2" || formData.role === "7" ? (
        <div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
          <label style={{
            marginBottom: 8,
            fontWeight: 500,
            color: "#495057",
            fontSize: isMobile ? 14 : 16
          }}>District *</label>
          <select
            value={selectedDistrict}
            onChange={(e) => handleFormChange("district", e.target.value)}
            disabled={!selectedDivision}
            required
            style={{
              padding: isMobile ? 10 : 12,
              border: "1px solid #ced4da",
              borderRadius: 6,
              fontSize: isMobile ? 14 : 16
            }}
          >
            <option value="">Select District</option>
            {districtOptions.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>
      ) : (
        // For Divisional Officer (role=3), show district as disabled/not required
        <div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
          <label style={{
            marginBottom: 8,
            fontWeight: 500,
            color: "#495057",
            fontSize: isMobile ? 14 : 16
          }}>
            District 
            {/* <span style={{ fontSize: 12, color: "#6c757d", marginLeft: 8 }}>(Not required for this role)</span> */}
          </label>
          <select
            disabled
            style={{
              padding: isMobile ? 10 : 12,
              border: "1px solid #ced4da",
              borderRadius: 6,
              fontSize: isMobile ? 14 : 16,
              backgroundColor: "#e9ecef",
              cursor: "not-allowed",
              color: "#6c757d"
            }}
          >
            {/* <option value="">District selection not required for Divisional Officer</option> */}
          </select>
        </div>
      )}
    </div>
  </div>
)}

                    {/* ULB Information Section */}
                    {(formData.role === "1" || formData.role === "7") && selectedDistrict && ulbOptions.length > 0 && (
                      <div className="form-section" style={{
                        marginBottom: 24,
                        padding: isMobile ? 16 : 20,
                        background: "#f8f9fa",
                        borderRadius: 8,
                        border: "1px solid #e9ecef"
                      }}>
                        <h4 style={{
                          margin: "0 0 16px 0",
                          color: "#000000",
                          borderBottom: "2px solid #113F67",
                          paddingBottom: 8,
                          fontSize: isMobile ? 16 : 18,
                          fontWeight: 600
                        }}>
                          <ApartmentOutlined style={{ marginRight: 8, color: "#113F67" }} />
                          ULB Information
                        </h4>
                        <div style={{
                          display: "grid",
                          gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(250px, 1fr))",
                          gap: isMobile ? 12 : 20
                        }}>
                          <div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
                            <label style={{
                              marginBottom: 8,
                              fontWeight: 500,
                              color: "#495057",
                              fontSize: isMobile ? 14 : 16
                            }}>Select ULB *</label>
                            <select
                              value={selectedUlb}
                              onChange={(e) => handleFormChange("ulb_id", e.target.value)}
                              required
                              style={{
                                padding: isMobile ? 10 : 12,
                                border: "1px solid #ced4da",
                                borderRadius: 6,
                                fontSize: isMobile ? 14 : 16
                              }}
                            >
                              <option value="">Select ULB</option>
                              {ulbOptions.map((ulb) => (
                                <option key={ulb.id} value={ulb.id}>{ulb.ulb_name}</option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
                            <label style={{
                              marginBottom: 8,
                              fontWeight: 500,
                              color: "#495057",
                              fontSize: isMobile ? 14 : 16
                            }}>ULB Name</label>
                            <input
                              type="text"
                              value={formData.ulb_name}
                              readOnly
                              placeholder="Auto-filled when ULB selected"
                              style={{
                                padding: isMobile ? 10 : 12,
                                border: "1px solid #ced4da",
                                borderRadius: 6,
                                fontSize: isMobile ? 14 : 16,
                                backgroundColor: "#e9ecef",
                                cursor: "not-allowed"
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* State-only Section for DMA roles */}
                    {formData.role && DMA_ROLES.includes(parseInt(formData.role)) && (
                      <div className="form-section" style={{
                        marginBottom: 24,
                        padding: isMobile ? 16 : 20,
                        background: "#f8f9fa",
                        borderRadius: 8,
                        border: "1px solid #e9ecef"
                      }}>
                        <h4 style={{
                          margin: "0 0 16px 0",
                          color: "#000000",
                          borderBottom: "2px solid #113F67",
                          paddingBottom: 8,
                          fontSize: isMobile ? 16 : 18,
                          fontWeight: 600
                        }}>
                          <EnvironmentOutlined style={{ marginRight: 8, color: "#113F67" }} />
                          Location
                        </h4>
                        <div style={{
                          display: "grid",
                          gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(250px, 1fr))",
                          gap: isMobile ? 12 : 20
                        }}>
                          <div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
                            <label style={{
                              marginBottom: 8,
                              fontWeight: 500,
                              color: "#495057",
                              fontSize: isMobile ? 14 : 16
                            }}>State *</label>
                            <select
                              value={selectedState}
                              onChange={(e) => handleFormChange("state", e.target.value)}
                              required
                              style={{
                                padding: isMobile ? 10 : 12,
                                border: "1px solid #ced4da",
                                borderRadius: 6,
                                fontSize: isMobile ? 14 : 16
                              }}
                            >
                              <option value="">Select State</option>
                              {stateOptions.map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}

                    <button
                      type="submit"
                      className="submit-btn"
                      disabled={loading || (DMA_ROLES.includes(parseInt(formData.role)) && formData.desk_ids.length === 0)}
                      style={{
                        background: "linear-gradient(45deg, #113F67, #113F67)",
                        color: "white",
                        border: "none",
                        padding: isMobile ? "14px 24px" : "16px 32px",
                        fontSize: isMobile ? 16 : 18,
                        fontWeight: 600,
                        borderRadius: 8,
                        cursor: "pointer",
                        width: "100%",
                        transition: "transform 0.2s",
                        opacity: (DMA_ROLES.includes(parseInt(formData.role)) && formData.desk_ids.length === 0) ? 0.6 : 1
                      }}
                    >
                      {loading ? "Creating..." :
                        (DMA_ROLES.includes(parseInt(formData.role)) && formData.desk_ids.length === 0)
                          ? "Please select at least one desk"
                          : "Create User Account"}
                    </button>
                  </form>
                </div>
              )}

              {/* ========== BULK UPLOAD TAB ========== */}
              {activeTab === "bulk" && (
                <div className="bulk-upload-tab">
                  <div className="alert alert-info" style={{
                    padding: isMobile ? 12 : 16,
                    borderRadius: 8,
                    marginBottom: 24,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    background: "#d1ecf1",
                    border: "1px solid #bee5eb",
                    color: "#0c5460",
                    fontSize: isMobile ? 13 : 14
                  }}>
                    <InfoCircleOutlined />
                    <span>Upload a CSV file to create multiple users at once. Make sure to include the <strong>designation</strong> and <strong>desk_ids</strong> (comma-separated) columns for DMA roles.</span>
                  </div>

                  <div style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(300px, 1fr))",
                    gap: 24
                  }}>
                    <div className="template-card" style={{
                      padding: isMobile ? 16 : 24,
                      background: "#f8f9fa",
                      borderRadius: 8,
                      border: "1px solid #e9ecef",
                      marginBottom: isMobile ? 16 : 0
                    }}>
                      <h4 style={{
                        margin: "0 0 20px 0",
                        color: "#000000",
                        fontSize: isMobile ? 16 : 18,
                        fontWeight: 600
                      }}>
                        <DownloadOutlined style={{ marginRight: 8, color: "#113F67" }} />
                        Download Templates
                      </h4>

                      <div className="divider" style={{
                        textAlign: "center",
                        margin: "20px 0",
                        color: "#6c757d",
                        position: "relative",
                        fontSize: isMobile ? 13 : 14
                      }}>
                        Role-Specific Templates (with Designation, Name & Status columns)
                      </div>

                      <div style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(auto-fill, minmax(120px, 1fr))",
                        gap: 12
                      }}>
                        {creatableRoles.map(role => (  // Change from roleOptions to creatableRoles
                          <div
                            key={role.value}
                            className="template-item"
                            onClick={() => downloadTemplate(role.value)}
                            style={{
                              background: "white",
                              border: "1px solid #e9ecef",
                              borderRadius: 8,
                              padding: isMobile ? 12 : 16,
                              textAlign: "center",
                              cursor: "pointer",
                              transition: "all 0.3s",
                              minHeight: isMobile ? "80px" : "auto"
                            }}
                          >
                            <div style={{
                              fontSize: isMobile ? 20 : 24,
                              marginBottom: 8,
                              color: "#113F67"
                            }}>
                              <FileTextOutlined />
                            </div>
                            <div style={{
                              fontWeight: 500,
                              marginBottom: 4,
                              fontSize: isMobile ? 13 : 14,
                              color: "#000000"
                            }}>{role.label}</div>
                            <small style={{
                              fontSize: isMobile ? 11 : 12,
                              color: "#666"
                            }}>
                              {DMA_ROLES.includes(role.value) ? "CSV with Multiple Desks" : "CSV Template"}
                            </small>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="upload-card" style={{
                      padding: isMobile ? 16 : 24,
                      background: "#f8f9fa",
                      borderRadius: 8,
                      border: "1px solid #e9ecef"
                    }}>
                      <h4 style={{
                        margin: "0 0 20px 0",
                        color: "#000000",
                        fontSize: isMobile ? 16 : 18,
                        fontWeight: 600
                      }}>
                        <UploadOutlined style={{ marginRight: 8, color: "#113F67" }} />
                        Upload CSV File
                      </h4>

                      <div className="file-upload" style={{ marginBottom: 20 }}>
                        <input
                          type="file"
                          accept=".csv"
                          onChange={(e) => setCsvFile(e.target.files[0])}
                          id="csv-upload"
                          style={{ display: "none" }}
                        />
                        <label htmlFor="csv-upload" style={{
                          display: "block",
                          padding: isMobile ? "30px 16px" : "40px 20px",
                          border: "2px dashed #dee2e6",
                          borderRadius: 8,
                          textAlign: "center",
                          cursor: "pointer",
                          transition: "border-color 0.3s"
                        }}>
                          <span style={{
                            display: "block",
                            marginBottom: 8,
                            fontSize: isMobile ? 14 : 16
                          }}>
                            <UploadOutlined style={{ fontSize: 24, color: "#113F67" }} />
                          </span>
                          <span style={{
                            display: "block",
                            marginBottom: 8,
                            fontSize: isMobile ? 14 : 16
                          }}>Choose CSV File</span>
                          <small style={{
                            color: "#6c757d",
                            fontSize: isMobile ? 12 : 14
                          }}>
                            Click or drag file here<br />
                            <strong>Include 'designation', 'name' and 'desk_ids' (comma-separated) columns</strong>
                          </small>
                        </label>
                      </div>

                      {csvFile && (
                        <div className="file-info" style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: isMobile ? 12 : 16,
                          background: "white",
                          border: "1px solid #e9ecef",
                          borderRadius: 8,
                          marginBottom: 20
                        }}>
                          <div style={{ fontSize: isMobile ? 20 : 24, color: "#113F67" }}>
                            <FileTextOutlined />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontWeight: "bold",
                              fontSize: isMobile ? 13 : 14,
                              color: "#000000"
                            }}>{csvFile.name}</div>
                            <div style={{
                              fontSize: isMobile ? 11 : 12,
                              color: "#666"
                            }}>
                              {(csvFile.size / 1024).toFixed(2)} KB
                            </div>
                          </div>
                          <button
                            className="btn-danger"
                            onClick={() => setCsvFile(null)}
                            style={{
                              background: "#e74c3c",
                              color: "white",
                              border: "none",
                              padding: isMobile ? "6px 10px" : "8px 12px",
                              borderRadius: 6,
                              cursor: "pointer",
                              fontSize: isMobile ? 12 : 14
                            }}
                          >
                            <CloseOutlined />
                          </button>
                        </div>
                      )}

                      <div className="action-buttons" style={{
                        display: "flex",
                        gap: 12,
                        flexDirection: isMobile ? "column" : "row"
                      }}>
                        {/* <button
                          className="btn-primary"
                          onClick={previewCsv}
                          disabled={!csvFile || bulkLoading}
                          style={{
                            background: "#6c757d",
                            color: "white",
                            border: "none",
                            padding: isMobile ? "12px 16px" : "12px 24px",
                            borderRadius: 6,
                            fontWeight: 500,
                            cursor: "pointer",
                            flex: 1,
                            fontSize: isMobile ? 14 : 16
                          }}
                        >
                          {bulkLoading ? "Parsing..." : "Preview CSV"}
                        </button> */}
                        <button
                          className="btn-primary"
                          onClick={onBulkUpload}
                          disabled={!csvFile || bulkLoading}
                          style={{
                            background: "linear-gradient(45deg, #113F67, #3c90daff)",
                            color: "white",
                            border: "none",
                            padding: isMobile ? "12px 16px" : "12px 24px",
                            borderRadius: 6,
                            fontWeight: 500,
                            cursor: "pointer",
                            flex: 1,
                            fontSize: isMobile ? 14 : 16
                          }}
                        >
                          {bulkLoading ? "Uploading..." : "Upload CSV"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* CSV PREVIEW */}
                  {csvPreview.length > 0 && previewVisible && (
                    <div className="csv-preview" style={{
                      marginTop: 24,
                      padding: isMobile ? 16 : 20,
                      background: "#fff",
                      borderRadius: 8,
                      border: "1px solid #e9ecef"
                    }}>
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 16
                      }}>
                        <h4 style={{
                          margin: 0,
                          fontSize: isMobile ? 16 : 18,
                          color: "#000000",
                          fontWeight: 600
                        }}>
                          <EyeOutlined style={{ marginRight: 8, color: "#113F67" }} />
                          CSV Preview (First 20 rows)
                        </h4>
                        <button
                          onClick={() => setPreviewVisible(false)}
                          style={{
                            background: "none",
                            border: "none",
                            fontSize: 20,
                            cursor: "pointer",
                            color: "#666"
                          }}
                        >
                          <CloseOutlined />
                        </button>
                      </div>
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: isMobile ? 12 : 14 }}>
                          <thead>
                            <tr>
                              <th style={{ padding: 8, border: "1px solid #dee2e6", background: "#f8f9fa" }}>#</th>
                              <th style={{ padding: 8, border: "1px solid #dee2e6", background: "#f8f9fa" }}>Username</th>
                              <th style={{ padding: 8, border: "1px solid #dee2e6", background: "#f8f9fa" }}>Full Name</th>
                              <th style={{ padding: 8, border: "1px solid #dee2e6", background: "#f8f9fa" }}>Email</th>
                              <th style={{ padding: 8, border: "1px solid #dee2e6", background: "#f8f9fa" }}>Role</th>
                              <th style={{ padding: 8, border: "1px solid #dee2e6", background: "#f8f9fa" }}>Designation</th>
                              <th style={{ padding: 8, border: "1px solid #dee2e6", background: "#f8f9fa" }}>Desk IDs</th>
                              <th style={{ padding: 8, border: "1px solid #dee2e6", background: "#f8f9fa" }}>Desk Names</th>
                              <th style={{ padding: 8, border: "1px solid #dee2e6", background: "#f8f9fa" }}>State</th>
                              <th style={{ padding: 8, border: "1px solid #dee2e6", background: "#f8f9fa" }}>Form Access</th>
                              <th style={{ padding: 8, border: "1px solid #dee2e6", background: "#f8f9fa" }}>Status</th>
                              <th style={{ padding: 8, border: "1px solid #dee2e6", background: "#f8f9fa" }}>Valid</th>
                            </tr>
                          </thead>
                          <tbody>
                            {csvPreview.map((row) => (
                              <tr key={row.key}>
                                <td style={{ padding: 8, border: "1px solid #dee2e6" }}>{row.row_index}</td>
                                <td style={{ padding: 8, border: "1px solid #dee2e6" }}>{row.username}</td>
                                <td style={{ padding: 8, border: "1px solid #dee2e6" }}>{row.full_name}</td>
                                <td style={{ padding: 8, border: "1px solid #dee2e6" }}>{row.email}</td>
                                <td style={{ padding: 8, border: "1px solid #dee2e6" }}>{row.role_name}</td>
                                <td style={{ padding: 8, border: "1px solid #dee2e6" }}>
                                  {row.designation !== "Not specified" ? (
                                    <span style={{ fontWeight: 500 }}>{row.designation}</span>
                                  ) : (
                                    <span style={{ color: "#6c757d", fontStyle: "italic" }}>Not specified</span>
                                  )}
                                </td>
                                <td style={{ padding: 8, border: "1px solid #dee2e6" }}>
                                  {row.desk_ids !== "None" ? (
                                    <span style={{ fontWeight: 500 }}>{row.desk_ids}</span>
                                  ) : (
                                    <span style={{ color: "#6c757d" }}>-</span>
                                  )}
                                </td>
                                <td style={{ padding: 8, border: "1px solid #dee2e6" }}>
                                  {row.desk_names !== "Not assigned" ? (
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                      {row.desk_names.split(', ').map((name, idx) => (
                                        <span
                                          key={idx}
                                          style={{
                                            display: "inline-block",
                                            padding: "2px 8px",
                                            background: "#e8f5e9",
                                            color: "#2e7d32",
                                            borderRadius: 12,
                                            fontSize: 11
                                          }}
                                        >
                                          {name}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span style={{ color: "#6c757d", fontStyle: "italic" }}>Not assigned</span>
                                  )}
                                </td>
                                <td style={{ padding: 8, border: "1px solid #dee2e6" }}>{row.state_name}</td>
                                <td style={{ padding: 8, border: "1px solid #dee2e6" }}>{row.has_form_builder_access}</td>
                                <td style={{ padding: 8, border: "1px solid #dee2e6" }}>
                                  <span style={{
                                    padding: "2px 8px",
                                    borderRadius: 12,
                                    fontSize: 11,
                                    fontWeight: 500,
                                    background: row.status === 'active' ? "#d4edda" : "#fff3cd",
                                    color: row.status === 'active' ? "#155724" : "#856404"
                                  }}>
                                    {row.status}
                                  </span>
                                </td>
                                <td style={{ padding: 8, border: "1px solid #dee2e6" }}>
                                  <span style={{
                                    padding: "2px 8px",
                                    borderRadius: 12,
                                    fontSize: 11,
                                    fontWeight: 500,
                                    background: row.isValid === "READY" ? "#d4edda" : "#f8d7da",
                                    color: row.isValid === "READY" ? "#155724" : "#721c24"
                                  }}>
                                    {row.isValid === "READY" ? <CheckOutlined /> : <WarningOutlined />} {row.isValid}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Upload Progress */}
                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div style={{ marginTop: 20 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span>Uploading...</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div style={{
                        width: "100%",
                        height: 8,
                        background: "#e9ecef",
                        borderRadius: 4,
                        overflow: "hidden"
                      }}>
                        <div style={{
                          width: `${uploadProgress}%`,
                          height: "100%",
                          background: "linear-gradient(45deg, #113F67, #3498db)",
                          transition: "width 0.3s"
                        }} />
                      </div>
                    </div>
                  )}

                  {/* Upload Summary */}
                  {uploadSummary && (
                    <div style={{
                      marginTop: 20,
                      padding: 16,
                      background: uploadSummary.failed_count > 0 ? "#fff3cd" : "#d4edda",
                      border: `1px solid ${uploadSummary.failed_count > 0 ? "#ffeeba" : "#c3e6cb"}`,
                      borderRadius: 8,
                      color: uploadSummary.failed_count > 0 ? "#856404" : "#155724"
                    }}>
                      <h5 style={{ margin: "0 0 8px 0", fontSize: isMobile ? 15 : 16, color: "#000000" }}>
                        {uploadSummary.failed_count > 0 ? <WarningOutlined style={{ marginRight: 8 }} /> : <CheckOutlined style={{ marginRight: 8 }} />}
                        {uploadSummary.failed_count > 0 ? "Upload Completed with Errors" : "Upload Successful"}
                      </h5>
                      <p style={{ margin: 0, fontSize: isMobile ? 13 : 14 }}>
                        Success: {uploadSummary.success_count} | Failed: {uploadSummary.failed_count}
                      </p>
                      {uploadSummary.errors.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <strong>Errors:</strong>
                          <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
                            {uploadSummary.errors.slice(0, 5).map((err, idx) => (
                              <li key={idx} style={{ fontSize: isMobile ? 12 : 13 }}>{err}</li>
                            ))}
                            {uploadSummary.errors.length > 5 && (
                              <li style={{ fontSize: isMobile ? 12 : 13 }}>...and {uploadSummary.errors.length - 5} more</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ========== VIEW USERS TAB ========== */}
              {activeTab === "view" && (
                <div className="view-users-tab">
                  {/* Desktop Header */}
                  {!isMobile && (
                    <div style={{
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      gap: isMobile ? 16 : 0,
                      justifyContent: "space-between",
                      alignItems: isMobile ? "stretch" : "center",
                      marginBottom: 24,
                      flexWrap: "wrap"
                    }}>
                      <div>
                        <h4 style={{
                          margin: 0,
                          color: "#000000",
                          fontSize: isMobile ? 16 : 18,
                          fontWeight: 600
                        }}>
                          <EyeOutlined style={{ marginRight: 8, color: "#113F67" }} />
                          User Directory
                        </h4>
                        <p style={{
                          margin: "4px 0 0 0",
                          color: "#4b5563",
                          fontSize: isMobile ? 12 : 14
                        }}>
                          {filteredUsers.length} users shown • {usersList.length} total users
                          {filteredUsers.filter(u => u.designation).length > 0 && (
                            <span style={{
                              marginLeft: 8,
                              fontWeight: 500,
                              color: "#27ae60",
                              fontSize: isMobile ? 12 : 14
                            }}>
                              • {filteredUsers.filter(u => u.designation).length} with designation
                            </span>
                          )}
                          {filteredUsers.filter(u => u.desk_assignments?.length > 0).length > 0 && (
                            <span style={{
                              marginLeft: 8,
                              fontWeight: 500,
                              color: "#e67e22",
                              fontSize: isMobile ? 12 : 14
                            }}>
                              • {filteredUsers.filter(u => u.desk_assignments?.length > 0).length} with desk
                            </span>
                          )}
                          {filteredUsers.filter(u => u.status).length > 0 && (
                            <span style={{
                              marginLeft: 8,
                              fontWeight: 500,
                              color: "#9b59b6",
                              fontSize: isMobile ? 12 : 14
                            }}>
                              • Status available
                            </span>
                          )}
                          {roleFilter && (
                            <span style={{
                              marginLeft: 8,
                              fontWeight: 500,
                              color: "#113F67",
                              fontSize: isMobile ? 12 : 14
                            }}>
                              • Filtered by: {roleOptions.find(r => r.value === parseInt(roleFilter))?.label}
                            </span>
                          )}
                        </p>
                      </div>
                      <div style={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        gap: 8,
                        width: isMobile ? "100%" : "auto"
                      }}>
                        <select
                          value={roleFilter}
                          onChange={(e) => setRoleFilter(e.target.value)}
                          style={{
                            padding: isMobile ? "10px 12px" : "12px 16px",
                            border: "1px solid #ced4da",
                            borderRadius: 6,
                            minWidth: isMobile ? "100%" : 180,
                            background: "white",
                            cursor: "pointer",
                            fontSize: isMobile ? 13 : 14
                          }}
                        >
                          <option value="">All Roles</option>
                          {roleOptions.map((role) => (
                            <option key={role.value} value={role.value}>
                              {role.label}
                            </option>
                          ))}
                        </select>

                        <input
                          type="text"
                          placeholder="Search by name, username, email, designation, ULB, or desk..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="search-input"
                          style={{
                            padding: isMobile ? "10px 12px" : 12,
                            border: "1px solid #ced4da",
                            borderRadius: 6,
                            minWidth: isMobile ? "100%" : 250,
                            fontSize: isMobile ? 13 : 14
                          }}
                        />

                        {(search || roleFilter) && (
                          <button
                            className="btn-secondary"
                            onClick={clearFilters}
                            style={{
                              background: "#6c757d",
                              color: "white",
                              border: "none",
                              padding: isMobile ? "10px 12px" : "12px 16px",
                              borderRadius: 6,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 6,
                              fontSize: isMobile ? 13 : 14
                            }}
                          >
                            <CloseOutlined /> Clear Filters
                          </button>
                        )}

                        <button
                          className="btn-primary"
                          onClick={fetchUsers}
                          disabled={viewLoading}
                          style={{
                            background: "linear-gradient(45deg, #113F67, #3c90daff)",
                            color: "white",
                            border: "none",
                            padding: isMobile ? "10px 12px" : "12px 16px",
                            borderRadius: 6,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            fontSize: isMobile ? 13 : 14
                          }}
                        >
                          <ReloadOutlined /> {viewLoading ? "Loading..." : "Refresh"}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Mobile Summary */}
                  {isMobile && !showMobileSearch && !showMobileFilters && (
                    <div style={{ marginBottom: 16 }}>
                      <h4 style={{
                        margin: "0 0 8px 0",
                        color: "#000000",
                        fontSize: 16,
                        fontWeight: 600
                      }}>
                        <EyeOutlined style={{ marginRight: 8, color: "#113F67" }} />
                        User Directory
                      </h4>
                      <p style={{
                        margin: 0,
                        color: "#4b5563",
                        fontSize: 13
                      }}>
                        {filteredUsers.length} of {usersList.length} users
                        {filteredUsers.filter(u => u.designation).length > 0 && (
                          <span style={{
                            display: "block",
                            marginTop: 4,
                            fontWeight: 500,
                            color: "#27ae60"
                          }}>
                            {filteredUsers.filter(u => u.designation).length} with designation
                          </span>
                        )}
                        {filteredUsers.filter(u => u.desk_assignments?.length > 0).length > 0 && (
                          <span style={{
                            display: "block",
                            marginTop: 4,
                            fontWeight: 500,
                            color: "#e67e22"
                          }}>
                            {filteredUsers.filter(u => u.desk_assignments?.length > 0).length} with desk
                          </span>
                        )}
                        {roleFilter && (
                          <span style={{
                            display: "block",
                            marginTop: 4,
                            fontWeight: 500,
                            color: "#113F67"
                          }}>
                            Filter: {roleOptions.find(r => r.value === parseInt(roleFilter))?.label}
                          </span>
                        )}
                      </p>
                    </div>
                  )}

                  {/* Role Statistics Bar */}
                  <div style={{
                    display: "flex",
                    gap: 8,
                    marginBottom: 16,
                    flexWrap: "wrap",
                    overflowX: isMobile ? "auto" : "visible",
                    paddingBottom: isMobile ? 8 : 0
                  }}>
                    <div
                      className={`role-stat-item ${!roleFilter ? "active" : ""}`}
                      onClick={() => setRoleFilter("")}
                      style={{
                        padding: isMobile ? "6px 12px" : "8px 16px",
                        background: !roleFilter ? "#113F67" : "#f8f9fa",
                        color: !roleFilter ? "white" : "#495057",
                        borderRadius: 20,
                        fontSize: isMobile ? 12 : 14,
                        fontWeight: 500,
                        cursor: "pointer",
                        border: "1px solid #e9ecef",
                        transition: "all 0.3s",
                        whiteSpace: "nowrap"
                      }}
                    >
                      All ({usersList.length})
                    </div>
                    {roleOptions.map((role) => (
                      <div
                        key={role.value}
                        className={`role-stat-item ${roleFilter === String(role.value) ? "active" : ""}`}
                        onClick={() => setRoleFilter(String(role.value))}
                        style={{
                          padding: isMobile ? "6px 12px" : "8px 16px",
                          background: roleFilter === String(role.value) ? "#113F67" : "#f8f9fa",
                          color: roleFilter === String(role.value) ? "white" : "#495057",
                          borderRadius: 20,
                          fontSize: isMobile ? 12 : 14,
                          fontWeight: 500,
                          cursor: "pointer",
                          border: "1px solid #e9ecef",
                          transition: "all 0.3s",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {isMobile ? role.label.split(' ')[0] : role.label} ({roleStats[role.value] || 0})
                      </div>
                    ))}
                  </div>

                  {/* Users Table */}
                  {viewLoading ? (
                    <div className="loading-spinner" style={{ textAlign: "center", padding: 40 }}>
                      <div className="spinner" style={{
                        width: 40,
                        height: 40,
                        border: "4px solid #f3f3f3",
                        borderTop: "4px solid #3498db",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        margin: "0 auto 16px"
                      }}></div>
                      <p>Loading users...</p>
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="no-users" style={{
                      textAlign: "center",
                      padding: isMobile ? 40 : 60,
                      color: "#6c757d"
                    }}>
                      <div style={{
                        fontSize: isMobile ? 36 : 48,
                        marginBottom: 16,
                        color: "#113F67"
                      }}>
                        <UserOutlined />
                      </div>
                      <h3 style={{ fontSize: isMobile ? 18 : 24, color: "#000000" }}>No users found</h3>
                      <p style={{ fontSize: isMobile ? 13 : 14 }}>
                        {search || roleFilter
                          ? "Try adjusting your search or filter criteria"
                          : "No users have been created yet"}
                      </p>
                      {(search || roleFilter) && (
                        <button
                          onClick={clearFilters}
                          style={{
                            marginTop: 16,
                            padding: "10px 20px",
                            background: "#113F67",
                            color: "white",
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                            fontSize: isMobile ? 13 : 14
                          }}
                        >
                          Clear Filters
                        </button>
                      )}
                    </div>
                  ) : (
                    <div style={{
                      overflowX: "auto",
                      WebkitOverflowScrolling: "touch",
                      margin: isMobile ? "0 -16px" : "0",
                      width: isMobile ? "100vw" : "100%",
                      maxWidth: isMobile ? "100%" : "100%"
                    }}>
                      <table style={{
                        width: isMobile ? "max-content" : "100%",
                        borderCollapse: "collapse",
                        fontSize: isMobile ? 12 : 14,
                        minWidth: isMobile ? "1200px" : "100%"
                      }}>
                        <thead>
                          <tr>
                            <th style={{
                              padding: isMobile ? 8 : 12,
                              border: "1px solid #e9ecef",
                              textAlign: "left",
                              background: "#f8f9fa",
                              fontWeight: 600,
                              fontSize: isMobile ? 12 : 14
                            }}>User</th>
                            <th style={{
                              padding: isMobile ? 8 : 12,
                              border: "1px solid #e9ecef",
                              textAlign: "left",
                              background: "#f8f9fa",
                              fontWeight: 600,
                              fontSize: isMobile ? 12 : 14
                            }}>Full Name</th>
                            <th style={{
                              padding: isMobile ? 8 : 12,
                              border: "1px solid #e9ecef",
                              textAlign: "left",
                              background: "#f8f9fa",
                              fontWeight: 600,
                              fontSize: isMobile ? 12 : 14
                            }}>Designation</th>
                            <th style={{
                              padding: isMobile ? 8 : 12,
                              border: "1px solid #e9ecef",
                              textAlign: "left",
                              background: "#f8f9fa",
                              fontWeight: 600,
                              fontSize: isMobile ? 12 : 14
                            }}>Desk Assignments</th>
                            <th style={{
                              padding: isMobile ? 8 : 12,
                              border: "1px solid #e9ecef",
                              textAlign: "left",
                              background: "#f8f9fa",
                              fontWeight: 600,
                              fontSize: isMobile ? 12 : 14
                            }}>Role</th>
                            <th style={{
                              padding: isMobile ? 8 : 12,
                              border: "1px solid #e9ecef",
                              textAlign: "left",
                              background: "#f8f9fa",
                              fontWeight: 600,
                              fontSize: isMobile ? 12 : 14
                            }}>Status</th>
                            {/* <th style={{
                              padding: isMobile ? 8 : 12,
                              border: "1px solid #e9ecef",
                              textAlign: "left",
                              background: "#f8f9fa",
                              fontWeight: 600,
                              fontSize: isMobile ? 12 : 14
                            }}>Location</th> */}
                            <th style={{
                              padding: isMobile ? 8 : 12,
                              border: "1px solid #e9ecef",
                              textAlign: "left",
                              background: "#f8f9fa",
                              fontWeight: 600,
                              fontSize: isMobile ? 12 : 14
                            }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredUsers.map((user) => {
                            const roleInfo = roleOptions.find(r => r.value === parseInt(user.role));
                            const deskAssignments = user.desk_assignments || [];
                            return (
                              <tr key={user.username} style={{ borderBottom: "1px solid #e9ecef" }}>
                                <td style={{
                                  padding: isMobile ? 8 : 12,
                                  border: "1px solid #e9ecef"
                                }}>
                                  <div className="user-cell" style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: isMobile ? 8 : 12
                                  }}>
                                    <div className="user-avatar" style={{
                                      width: isMobile ? 32 : 40,
                                      height: isMobile ? 32 : 40,
                                      background: "linear-gradient(45deg, #113F67, #247dcaff)",
                                      color: "white",
                                      borderRadius: "50%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontWeight: "bold",
                                      fontSize: isMobile ? 14 : 16
                                    }}>
                                      {user.username?.charAt(0)?.toUpperCase() || 'U'}
                                    </div>
                                    <div>
                                      <div className="username" style={{
                                        fontWeight: 600,
                                        fontSize: isMobile ? 13 : 14,
                                        color: "#000000"
                                      }}>{user.username}</div>
                                      <div className="user-email" style={{
                                        fontSize: isMobile ? 11 : 12,
                                        color: "#4b5563"
                                      }}>{user.email}</div>
                                    </div>
                                  </div>
                                </td>
                                <td style={{
                                  padding: isMobile ? 8 : 12,
                                  border: "1px solid #e9ecef",
                                  fontSize: isMobile ? 13 : 14
                                }}>{user.full_name || user.name}</td>
                                <td style={{
                                  padding: isMobile ? 8 : 12,
                                  border: "1px solid #e9ecef",
                                  fontSize: isMobile ? 13 : 14
                                }}>
                                  {user.designation ? (
                                    <span style={{
                                      display: "inline-block",
                                      padding: "2px 8px",
                                      background: "#e8f5e9",
                                      color: "#2e7d32",
                                      borderRadius: 12,
                                      fontSize: isMobile ? 11 : 12,
                                      fontWeight: 500
                                    }}>
                                      {user.designation}
                                    </span>
                                  ) : (
                                    <span style={{ color: "#6c757d", fontStyle: "italic", fontSize: isMobile ? 11 : 12 }}>
                                      Not specified
                                    </span>
                                  )}
                                </td>
                                <td style={{
                                  padding: isMobile ? 8 : 12,
                                  border: "1px solid #e9ecef",
                                  fontSize: isMobile ? 13 : 14
                                }}>
                                  {deskAssignments.length > 0 ? (
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                      {deskAssignments.map((desk, idx) => {
                                        const deskColor = deskOptions.find(d => d.id === desk.id)?.color || DESK_COLORS[idx % DESK_COLORS.length];
                                        return (
                                          <span
                                            key={idx}
                                            style={{
                                              display: "inline-block",
                                              padding: "2px 8px",
                                              background: "#f0f7ff",
                                              border: `1px solid ${deskColor}`,
                                              color: "#004085",
                                              borderRadius: 12,
                                              fontSize: isMobile ? 10 : 11,
                                              fontWeight: 500
                                            }}
                                          >
                                            {desk.name}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <span style={{ color: "#6c757d", fontStyle: "italic", fontSize: isMobile ? 11 : 12 }}>
                                      Not assigned
                                    </span>
                                  )}
                                </td>
                                <td style={{
                                  padding: isMobile ? 8 : 12,
                                  border: "1px solid #e9ecef"
                                }}>
                                  <span className={`role-badge ${roleInfo?.color}`} style={{
                                    padding: isMobile ? "3px 8px" : "4px 12px",
                                    borderRadius: 20,
                                    fontSize: isMobile ? 11 : 12,
                                    fontWeight: 500,
                                    background: roleInfo?.color === "blue" ? "#d0ebff" :
                                      roleInfo?.color === "green" ? "#d3f9d8" :
                                        roleInfo?.color === "purple" ? "#e5dbff" :
                                          roleInfo?.color === "orange" ? "#ffe8cc" :
                                            roleInfo?.color === "red" ? "#ffe3e3" :
                                              roleInfo?.color === "pink" ? "#fce4ec" :
                                                roleInfo?.color === "darkblue" ? "#cce5ff" :
                                                  roleInfo?.color === "teal" ? "#d1f0eb" : "#e9ecef",
                                    color: roleInfo?.color === "blue" ? "#1971c2" :
                                      roleInfo?.color === "green" ? "#2b8a3e" :
                                        roleInfo?.color === "purple" ? "#6741d9" :
                                          roleInfo?.color === "orange" ? "#e67700" :
                                            roleInfo?.color === "red" ? "#c92a2a" :
                                              roleInfo?.color === "pink" ? "#c2185b" :
                                                roleInfo?.color === "darkblue" ? "#004085" :
                                                  roleInfo?.color === "teal" ? "#0d5e4a" : "#495057",
                                    display: "inline-block",
                                    whiteSpace: "nowrap"
                                  }}>
                                    {isMobile ? roleInfo?.label?.split(' ')[0] : roleInfo?.label}
                                  </span>
                                </td>
                                <td style={{
                                  padding: isMobile ? 8 : 12,
                                  border: "1px solid #e9ecef"
                                }}>
                                  <span style={{
                                    padding: "2px 8px",
                                    borderRadius: 12,
                                    fontSize: isMobile ? 11 : 12,
                                    fontWeight: 500,
                                    background: user.status === 'active' ? '#d4edda' :
                                      user.status === 'inactive' ? '#fff3cd' : '#f8d7da',
                                    color: user.status === 'active' ? '#155724' :
                                      user.status === 'inactive' ? '#856404' : '#721c24'
                                  }}>
                                    {user.status || 'active'}
                                  </span>
                                </td>
                                {/* <td style={{
                                  padding: isMobile ? 8 : 12,
                                  border: "1px solid #e9ecef"
                                }}>
                                  <div className="location-info" style={{
                                    fontSize: isMobile ? 11 : 12,
                                    color: "#495057"
                                  }}>
                                    {user.state_name && <div>State: {user.state_name}</div>}
                                    {user.district_name && <div>Dist: {user.district_name}</div>}
                                    {user.ulb_name && <div>ULB: {user.ulb_name}</div>}
                                  </div>
                                </td> */}
                                <td style={{
                                  padding: isMobile ? 8 : 12,
                                  border: "1px solid #e9ecef"
                                }}>
                                  <div className="action-buttons" style={{ display: "flex", gap: 8, flexDirection: isMobile ? "column" : "row" }}>
                                    <select
                                      onChange={(e) => updateUserStatus(user.username, e.target.value)}
                                      value={user.status || 'active'}
                                      style={{
                                        padding: isMobile ? "4px 6px" : "4px 8px",
                                        borderRadius: 4,
                                        border: "1px solid #dcdde1",
                                        fontSize: isMobile ? 11 : 12,
                                        cursor: "pointer"
                                      }}
                                    >
                                      <option value="active">Active</option>
                                      <option value="inactive">Inactive</option>
                                      <option value="suspended">Suspended</option>
                                    </select>
                                    <button
                                      onClick={() => deleteUser(user.username)}
                                      style={{
                                        color: "#2f3542",
                                        border: "1px solid #dcdde1",
                                        padding: isMobile ? "4px 8px" : "6px 12px",
                                        borderRadius: 8,
                                        cursor: "pointer",
                                        fontSize: isMobile ? 11 : 12,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 4
                                      }}
                                    >
                                      <DeleteOutlined /> {isMobile ? "" : "Remove"}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile CSS */}
      <style jsx>{`
        @media (max-width: ${BREAKPOINT}px) {
          .tabs-header::-webkit-scrollbar {
            display: none;
          }
          
          .table-container {
            -webkit-overflow-scrolling: touch;
          }
          
          button, select, input {
            min-height: 44px;
          }
          
          input, select, textarea {
            font-size: 16px;
          }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default CreateUserPage;